import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";
import {Badge, Col, Row, Spinner} from "react-bootstrap";
import AdvancedCombobox from "../../../primer/AdvancedCombobox";
import Button from "react-bootstrap/Button";
import NewClientModal from "../Clients/new-client-modal";
import {
    resetPatientHistory, setAutoPatientHistory,
    setPatientHistory,
    setPatientHistoryFirstLoad, setPatientHistoryOptions,
    setPatientHistoryPrescriptions,
    setPatientHistoryPrescriptionsRequestData,
    setPatientHistoryReferrals,
    setPatientHistoryReferralsRequestData,
    setPatientHistoryTreatmentProtocols, softResetPatientHistory
} from "../../../../_reducers/DataPanel/Prescription/prescriptionSlice";
import DateHelper from "../../../primer/DateHelper/DateHelper";
import DateBox from "../../../primer/DateBox";
import moment from "moment/moment";
import BetterCheckBox from "../../../common/BetterCheckBox";
import {bigTextComponent, formatPrescriptionStatus, textToLines} from "./constants";
import {toast} from "react-toastify";
import BiometricsModal from "./Components/biometricsModal";
import {openPrescription, openReferral} from "./prescriptionFunctions";
import PatientDetailsModal from "./Components/patientDetailsModal";
import {useTranslation} from "../../../../hooks/useTranslation";

const PatientHistory = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const companyDetails = useSelector((state) => state.COMPANY_DATA.companyDetails);
    const TABS_DATA = useSelector((state) => state.TABS_REDUCER);
    const patientHistory = useSelector((state) => state.PRESCRIPTION.patientHistory);
    const patientHistoryOptions = useSelector((state) => state.PRESCRIPTION.patientHistoryOptions);
    const patientHistoryPrescriptionsRequestData = useSelector((state) => state.PRESCRIPTION.patientHistoryPrescriptionsRequestData);
    const patientHistoryPrescriptions = useSelector((state) => state.PRESCRIPTION.patientHistoryPrescriptions);
    const patientHistoryReferralsRequestData = useSelector((state) => state.PRESCRIPTION.patientHistoryReferralsRequestData);
    const patientHistoryReferrals = useSelector((state) => state.PRESCRIPTION.patientHistoryReferrals);
    const patientHistoryTreatmentProtocols = useSelector((state) => state.PRESCRIPTION.patientHistoryTreatmentProtocols);
    const patientHistoryFirstLoad = useSelector((state) => state.PRESCRIPTION.patientHistoryFirstLoad);
    const doctorDetails = companyDetails?.prescriptionDoctorDetails;

    const [patientsList, setPatientsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingPrescriptions, setLoadingPrescriptions] = useState(false);
    const [loadingReferrals, setLoadingReferrals] = useState(false);

    const [referralsPageChanged, setReferralsPageChanged] = useState(false);
    const [prescriptionsPageChanged, setPrescriptionsPageChanged] = useState(false);
    const [biometricsModal, setBiometricsModal] = useState(false);
    const [showPatientDetails, setShowPatientDetails] = useState(false);

    const autoPatientHistory = useSelector((state) => state.PRESCRIPTION.autoPatientHistory);
    const [autoSearch, setAutoSearch] = useState(false);

    useEffect(() => {
        loadPatientsList(null, setPatientsList);
    }, []);

    useEffect(() => {
        if (autoSearch) {
            setAutoSearch(false);
            dispatch(softResetPatientHistory());
        }
    }, [autoSearch]);

    useEffect(() => {
        if (autoPatientHistory) {
            handleOnChange({row: autoPatientHistory}, "acdd", "clientDisplay");
            dispatch(setAutoPatientHistory(null));
            setAutoSearch(true);
        }
    }, [autoPatientHistory])

    useEffect(() => {
        if (referralsPageChanged) {
            setReferralsPageChanged(false);
            fetchReferrals(true);
        }
    }, [referralsPageChanged]);

    useEffect(() => {
        if (prescriptionsPageChanged) {
            setPrescriptionsPageChanged(false);
            fetchPrescriptions(true);
        }
    }, [prescriptionsPageChanged]);

    const patientDropdownColumns = [
        {
            label: "Ονοματεπώνυμο",
            name: "name",
        },
        {
            label: "AMKA",
            name: "amka",
        },
    ]

    const loadPatientsList = (keyword, setData) => {
        let reqData = {
            company: company.id,
        }
        if (keyword) reqData.keyword = keyword;
        axios.get(`${process.env.REACT_APP_API_URL2}/prescription/list-patients`, {
            headers: { "Content-Type": "application/json" },
            params: reqData
        }).then((res) => {
            if (res.data.status === "200") {
                if (typeof setData === "function") setData(res.data.data);
            }
        }).catch((err) => {
            console.log(err);
        })
    }

    const handleOnChange = (e, type, name) => {
        if (type === "acdd") {
            if (name === "clientDisplay") {
                dispatch(setPatientHistory({...patientHistory, client: e.row._id, clientDisplay: `${e.row.name} (${e.row.amka})`, amka: e.row.amka}));
            }
        } else if (type === "db") {
            if (moment(e).isValid()) {
                dispatch(setPatientHistory({...patientHistory, [name] : moment(e).format("DD/MM/YYYY")}));
            } else {
                dispatch(setPatientHistory({...patientHistory, [name] : ""}));
            }
        } else if (type === "cb") {
            dispatch(setPatientHistory({...patientHistory, [name] : e.target.checked}))
        }
    }

    const handleResetPatientHistory = () => {
        dispatch(resetPatientHistory());
    }

    const hookNewPatient = (data) => {
        dispatch(setPatientHistory({...patientHistory, client: data._id, clientDisplay: `${data.name} (${data.amka})`, amka: data.amka}));
    }

    const fetchData = (fromPagination = false) => {
        const options = {
            tps: patientHistory.patientsConsentTP,
            biometrics: patientHistory.patientsConsentBiometrics,
            personal: patientHistory.patientsConsentPersonal,
        }
        if (patientHistoryFirstLoad) dispatch(setPatientHistoryFirstLoad(false));
        fetchPrescriptions(fromPagination);
        fetchReferrals(fromPagination);
        if (options.tps) fetchTreatmentProtocols();

        if (!options.tps) dispatch(setPatientHistoryTreatmentProtocols([]));
        dispatch(setPatientHistoryOptions({...patientHistoryOptions, ...options}));
    }

    const fetchPrescriptions = (fromPagination = false) => {
        setLoading(true);
        setLoadingPrescriptions(true);
        axios.get(`${process.env.REACT_APP_API_URL2}/prescription/patient-prescriptions`, {
            headers: { "Content-Type": "application/json" },
            params: {
                amka: patientHistory.amka,
                company: company.id,
                patientsConsent: patientHistory.patientsConsent,
                date_from: patientHistory.date_from,
                date_to: patientHistory.date_to,
                page: fromPagination ? patientHistoryPrescriptionsRequestData.page : 1,
                forceSize: 5,
            }
        }).then((res) => {
            setLoading(false);
            setLoadingPrescriptions(false);
            if (res.data.status === "200") {
                dispatch(setPatientHistoryPrescriptions(res.data.data));
                dispatch(setPatientHistoryPrescriptionsRequestData({
                    totalPages: res.data.totalPages,
                    totalItems: res.data.totalEntries,
                    page: fromPagination ? patientHistoryPrescriptionsRequestData.page : 1,
                }))
            } else {
                toast.error(res.data.message);
            }
        }).catch((err) => {
            setLoading(false);
            setLoadingPrescriptions(false);
            console.log(err);
            toast.error("Σφάλμα κατά την αποστολή αιτήματος.");
        })
    }

    const fetchReferrals = (fromPagination = false) => {
        setLoading(true);
        setLoadingReferrals(true);
        axios.get(`${process.env.REACT_APP_API_URL2}/prescription/patient-referrals`, {
            headers: { "Content-Type": "application/json" },
            params: {
                amka: patientHistory.amka,
                company: company.id,
                patientsConsent: patientHistory.patientsConsent,
                date_from: patientHistory.date_from,
                date_to: patientHistory.date_to,
                page: fromPagination ? patientHistoryReferralsRequestData.page : 1,
                forceSize: 5,
            }
        }).then((res) => {
            setLoading(false);
            setLoadingReferrals(false);
            if (res.data.status === "200") {
                dispatch(setPatientHistoryReferrals(res.data.data));
                dispatch(setPatientHistoryReferralsRequestData({
                    totalPages: res.data.totalPages,
                    totalItems: res.data.totalEntries,
                    page: fromPagination ? patientHistoryReferralsRequestData.page : 1,
                }))
            } else {
                toast.error(res.data.message);
            }
        }).catch((err) => {
            setLoading(false);
            setLoadingReferrals(false);
            console.log(err);
            toast.error("Σφάλμα κατά την αποστολή αιτήματος.");
        })
    }

    const fetchTreatmentProtocols = () => {
        axios.get(`${process.env.REACT_APP_API_URL2}/prescription/patient-treatment-protocols`, {
            headers: { 'Content-Type': 'application/json' },
            params: {
                company: company.id,
                amka: patientHistory.amka,
            }
        }).then((res) => {
            if (res.data.status === "200") {
                dispatch(setPatientHistoryTreatmentProtocols(res.data.data));
            }
        }).catch((err) => {
            console.log(err);
        })
    }

    const onPrescriptionSelect = (item) => {
        dispatch(openPrescription(item.barcode, TABS_DATA, {patientsConsent: patientHistory.patientsConsent, amka: patientHistory.amka}));
    }

    const onReferralSelect = (item) => {
        dispatch(openReferral(item.barcode, TABS_DATA, {patientsConsent: patientHistory.patientsConsent, amka: patientHistory.amka}))
    }

    const prescriptions_toFirstPage = () => {
        dispatch(setPatientHistoryPrescriptionsRequestData({...patientHistoryPrescriptionsRequestData, page: 1}));
        setPrescriptionsPageChanged(true);
    }

    const prescriptions_toPreviousPage = () => {
        dispatch(setPatientHistoryPrescriptionsRequestData({...patientHistoryPrescriptionsRequestData, page: patientHistoryPrescriptionsRequestData.page - 1}));
        setPrescriptionsPageChanged(true);
    }

    const prescriptions_toNextPage = () => {
        dispatch(setPatientHistoryPrescriptionsRequestData({...patientHistoryPrescriptionsRequestData, page: patientHistoryPrescriptionsRequestData.page + 1}));
        setPrescriptionsPageChanged(true);
    }

    const prescriptions_toLastPage = () => {
        dispatch(setPatientHistoryPrescriptionsRequestData({...patientHistoryPrescriptionsRequestData, page: patientHistoryPrescriptionsRequestData.totalPages}));
        setPrescriptionsPageChanged(true);
    }

    const referrals_toFirstPage = () => {
        dispatch(setPatientHistoryReferralsRequestData({...patientHistoryReferralsRequestData, page: 1}));
        setReferralsPageChanged(true);
    }

    const referrals_toPreviousPage = () => {
        dispatch(setPatientHistoryReferralsRequestData({...patientHistoryReferralsRequestData, page: patientHistoryReferralsRequestData.page - 1}));
        setReferralsPageChanged(true);
    }

    const referrals_toNextPage = () => {
        dispatch(setPatientHistoryReferralsRequestData({...patientHistoryReferralsRequestData, page: patientHistoryReferralsRequestData.page + 1}));
        setReferralsPageChanged(true);
    }

    const referrals_toLastPage = () => {
        dispatch(setPatientHistoryReferralsRequestData({...patientHistoryReferralsRequestData, page: patientHistoryReferralsRequestData.totalPages}));
        setReferralsPageChanged(true);
    }

    const patientSelected = String(patientHistory.client).length === 24;

    return (
        <div>
            <React.Fragment>
                <Row className={"mb-3"}>
                    <Col md={7}>
                        <Row>
                            <Col md={10}>
                                <AdvancedCombobox
                                    name={"clientDisplay"}
                                    className={"w-100 ac-select-dropdown"}
                                    defaultValue={patientHistory.clientDisplay}
                                    label={"Ασθενής"}
                                    data={patientsList}
                                    onChange={(e) => handleOnChange(e, "acdd", "clientDisplay")}
                                    key={patientHistory.client}
                                    onChangeSearch={(keyword, setData) => loadPatientsList(keyword, setData)}
                                    excludeColumns={[]}
                                    columns={patientDropdownColumns}
                                    deleteEnable={false}
                                    searchTableEnable={false}
                                    showTableOptionOnSelect={true}
                                    placeholder={"Πληκτρολογίστε και επιλέξτε ασθενή"}
                                    required={true}
                                    showInsertAddButton={true}
                                    hideSaveButton={true}
                                    hideInsertNewRow={true}
                                    inputFieldClasses="form-control"
                                    isPayrollCombobox={true}
                                    disabled={patientSelected}
                                />
                            </Col>
                            <Col md={2}>
                                {patientSelected ? (
                                    <Button title="Καθαρισμός πεδίου ασθενή" size={"sm"} onClick={() => handleResetPatientHistory()} style={{marginTop: "28px"}}>Καθαρισμός</Button>
                                ) : (
                                    <div style={{paddingTop: "23px"}}><NewClientModal fromPrescription={true} successCallback={(data) => hookNewPatient(data)} /></div>
                                )}
                            </Col>
                        </Row>
                    </Col>
                </Row>
                {patientSelected && (
                    <React.Fragment>
                        <Row>
                            <Col md={3}>
                                <DateHelper
                                    dateFromAndToFunction={(from, to) => dispatch(setPatientHistory({...patientHistory, date_from: from, date_to: to}))}
                                />
                            </Col>
                            <Col md={2}>
                                <DateBox
                                    name={"date_from"}
                                    label={"Ημερομηνία από"}
                                    selected={moment(patientHistory.date_from, "DD/MM/YYYY").isValid() ? moment(patientHistory.date_from, "DD/MM/YYYY").toDate() : ""}
                                    onChange={(e) => handleOnChange(e, "db", "date_from")}
                                    allowOtherYears={true}
                                />
                            </Col>
                            <Col md={2}>
                                <DateBox
                                    name={"date_to"}
                                    label={"Ημερομηνία έως"}
                                    selected={moment(patientHistory.date_to, "DD/MM/YYYY").isValid() ? moment(patientHistory.date_to, "DD/MM/YYYY").toDate() : ""}
                                    onChange={(e) => handleOnChange(e, "db", "date_to")}
                                    allowOtherYears={true}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <div style={{marginTop: "25px"}}>
                                    <BetterCheckBox
                                        name={"patientsConsent"}
                                        text={"Έχω την συγκατάθεση του ασθενή να δω το πλήρες ιστορικό του, από όλους τους παρόχους υγείας."}
                                        checked={patientHistory.patientsConsent}
                                        onChange={(e) => handleOnChange(e, "cb", "patientsConsent")}
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <div style={{marginTop: "25px"}}>
                                    <BetterCheckBox
                                        name={"patientsConsentBiometrics"}
                                        text={"Έχω την συγκατάθεση του ασθενή να δω τα βιομετρικά του."}
                                        checked={patientHistory.patientsConsentBiometrics}
                                        onChange={(e) => handleOnChange(e, "cb", "patientsConsentBiometrics")}
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <div style={{marginTop: "25px"}}>
                                    <BetterCheckBox
                                        name={"patientsConsentPersonal"}
                                        text={"Έχω την συγκατάθεση του ασθενή να δω τα προσωπικά του στοιχεία."}
                                        checked={patientHistory.patientsConsentPersonal}
                                        onChange={(e) => handleOnChange(e, "cb", "patientsConsentPersonal")}
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <div style={{marginTop: "25px"}}>
                                    <BetterCheckBox
                                        name={"patientsConsentTP"}
                                        text={"Έχω την συγκατάθεση του ασθενή να δω τα Θεραπευτικά Πρωτόκολλα του."}
                                        checked={patientHistory.patientsConsentTP}
                                        onChange={(e) => handleOnChange(e, "cb", "patientsConsentTP")}
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Button onClick={() => fetchData(false)} disabled={loading} style={{marginTop: "20px"}}>
                                    Αναζήτηση {loading && <Spinner className={"ml-2"} animation={"border"} variant={"dark"} />}
                                </Button>
                            </Col>
                        </Row>
                        {!patientHistoryFirstLoad && (
                            <React.Fragment>
                                <Row>
                                    <Col md={12}>
                                        {patientHistoryOptions.biometrics && (
                                            <Button size={"sm"} className={"mr-1"} onClick={() => setBiometricsModal(true)} style={{marginTop: "28px"}}>Βιομετρικά</Button>
                                        )}
                                        {patientHistoryOptions.personal && (
                                            <Button size={"sm"} onClick={() => setShowPatientDetails(true)} style={{marginTop: "28px"}}>Δεδομένα ασθενή</Button>
                                        )}
                                    </Col>
                                </Row>
                                <hr className={"mt-2 mb-2"} />
                                {patientHistoryOptions.tps && (
                                    <React.Fragment>
                                        <Row>
                                            <Col md={12}>
                                                <div style={{textAlign: "center"}}><h3>Θεραπευτικά Πρωτόκολλα</h3></div>
                                                {patientHistoryTreatmentProtocols.length > 0 ? (
                                                    <table className={"simpleClassicTable"}>
                                                        <thead>
                                                        <tr>
                                                            <th>Θεραπευτικό Πρωτόκολλο</th>
                                                            <th>Ημ/νία Ένταξης στο Πρωτόκολλο</th>
                                                            <th>MDC</th>
                                                            <th>Περιγραφή</th>
                                                            <th>Ημ/νία Τελευταίας Συνταγ/σης</th>
                                                            <th>Κατάσταση</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {patientHistoryTreatmentProtocols.map((tp, idx) => (
                                                            <tr key={`treatmentProtocols-${idx}`}>
                                                                <td>{tp?.treatmentProtocol?.code} - {tp?.treatmentProtocol?.title}</td>
                                                                <td>{moment(tp?.insertDate, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")}</td>
                                                                <td>{tp?.treatmentProtocol?.mdc.description}</td>
                                                                <td>{bigTextComponent(tp?.treatmentProtocol?.description)}</td>
                                                                <td>{tp?.lastUsedDate ? moment(tp?.lastUsedDate, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY") : "-"}</td>
                                                                <td>{tp.active === "true" ? <Badge bg={"success"}>Ενεργό</Badge> : <Badge bg={"danger"}>Ανενεργό</Badge>}</td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <span>Ο ασθενής δεν έχει/είχε θεραπευτικά πρωτόκολλα</span>
                                                )}
                                            </Col>
                                        </Row>
                                        <hr/>
                                    </React.Fragment>
                                )}
                                <Row>
                                    <Col md={12}>
                                        {loadingPrescriptions ? (
                                            <div style={{textAlign: "center"}}>
                                                <Spinner className={"ml-2"} size={"sm"} animation={"border"} variant={"dark"} /> Γίνεται φόρτωση...
                                            </div>
                                        ) : patientHistoryPrescriptionsRequestData.totalPages > 0 ? (
                                            <React.Fragment>
                                                <div style={{textAlign: "center"}}><h3>Συνταγές</h3></div>
                                                <Row>
                                                    <Col md={3}>
                                                        <div className={"text-muted"} style={{marginTop: "10px"}}>Σύνολο αποτελεσμάτων: {patientHistoryPrescriptionsRequestData.totalItems}</div>
                                                    </Col>
                                                    <Col md={9}>
                                                        <div className="d-flex justify-content-end mb-2">
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => prescriptions_toFirstPage()}
                                                                disabled={patientHistoryPrescriptionsRequestData.page === 1 || loading}
                                                            >
                                                                {t('SalesBalanceSheet.first')}
                                                            </Button>
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => prescriptions_toPreviousPage()}
                                                                disabled={patientHistoryPrescriptionsRequestData.page === 1 || loading}
                                                            >
                                                                {t('SalesBalanceSheet.previous')}
                                                            </Button>
                                                            <Badge style={{fontSize: "12px", lineHeight: "20px"}} className="mr-1"
                                                                   bg="primary">{t('SalesBalanceSheet.page')} {patientHistoryPrescriptionsRequestData.page} / {patientHistoryPrescriptionsRequestData.totalPages}</Badge>
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => prescriptions_toNextPage()}
                                                                disabled={patientHistoryPrescriptionsRequestData.page === patientHistoryPrescriptionsRequestData.totalPages || loading}
                                                            >
                                                                {t('SalesBalanceSheet.next')}
                                                            </Button>
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => prescriptions_toLastPage()}
                                                                disabled={patientHistoryPrescriptionsRequestData.page === patientHistoryPrescriptionsRequestData.totalPages || loading}
                                                            >
                                                                {t('SalesBalanceSheet.last')}
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={12}>
                                                        {patientHistoryPrescriptions.some((o) => doctorDetails?.amka !== o?.doctorAMKA) && (
                                                            <span>(*) Με πορτοκαλί χρωματίζονται οι συνταγές άλλων γιατρών</span>
                                                        )}
                                                        <table className={"simpleClassicTable prescriptionTable"}>
                                                            <colgroup>
                                                                <col span={1} style={{width: "20%"}}></col>
                                                                <col span={1} style={{width: "10%"}}></col>
                                                                <col span={1} style={{width: "20%"}}></col>
                                                                <col span={1} style={{width: "35%"}}></col>
                                                                <col span={1} style={{width: "15%"}}></col>
                                                            </colgroup>
                                                            <thead>
                                                            <tr>
                                                                <th>Barcode</th>
                                                                <th>Ημερομηνία Συντ/σης</th>
                                                                <th>Διαγνώσεις</th>
                                                                <th>Φάρμακα</th>
                                                                <th>Κατάσταση</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {patientHistoryPrescriptions.map((o, idx) => (
                                                                <tr key={`copyPrescr-${idx}`} onClick={() => onPrescriptionSelect(o)}
                                                                    style={{backgroundColor: doctorDetails?.amka !== o?.doctorAMKA ? "#ffe2b3" : ""}}>
                                                                    <td>{o.barcode}</td>
                                                                    <td>{o.issueDate}</td>
                                                                    <td>{textToLines(o.diagnoses)}</td>
                                                                    <td>{textToLines(o.drugs)}</td>
                                                                    <td>{formatPrescriptionStatus(o.status)}</td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </Col>
                                                </Row>
                                            </React.Fragment>
                                        ) : (
                                            <span>Δεν βρέθηκαν συνταγές</span>
                                        )}
                                    </Col>
                                </Row>
                                <hr/>
                                <Row className={"mt-2"}>
                                    <Col md={12}>
                                        {loadingReferrals ? (
                                            <div style={{textAlign: "center"}}>
                                                <Spinner className={"ml-2"} size={"sm"} animation={"border"} variant={"dark"} /> Γίνεται φόρτωση...
                                            </div>
                                        ) : patientHistoryReferralsRequestData.totalPages > 0 ? (
                                            <React.Fragment>
                                                <div style={{textAlign: "center"}}><h3>Παραπεμπτικά</h3></div>
                                                <Row>
                                                    <Col md={3}>
                                                        <div className={"text-muted"} style={{marginTop: "10px"}}>Σύνολο αποτελεσμάτων: {patientHistoryReferralsRequestData.totalItems}</div>
                                                    </Col>
                                                    <Col md={9}>
                                                        <div className="d-flex justify-content-end mb-2">
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => referrals_toFirstPage()}
                                                                disabled={patientHistoryReferralsRequestData.page === 1 || loading}
                                                            >
                                                                {t('SalesBalanceSheet.first')}
                                                            </Button>
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => referrals_toPreviousPage()}
                                                                disabled={patientHistoryReferralsRequestData.page === 1 || loading}
                                                            >
                                                                {t('SalesBalanceSheet.previous')}
                                                            </Button>
                                                            <Badge style={{fontSize: "12px", lineHeight: "20px"}} className="mr-1"
                                                                   bg="primary">{t('SalesBalanceSheet.page')} {patientHistoryReferralsRequestData.page} / {patientHistoryReferralsRequestData.totalPages}</Badge>
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => referrals_toNextPage()}
                                                                disabled={patientHistoryReferralsRequestData.page === patientHistoryReferralsRequestData.totalPages || loading}
                                                            >
                                                                {t('SalesBalanceSheet.next')}
                                                            </Button>
                                                            <Button
                                                                size={"sm"}
                                                                className={"mr-1"}
                                                                onClick={() => referrals_toLastPage()}
                                                                disabled={patientHistoryReferralsRequestData.page === patientHistoryReferralsRequestData.totalPages || loading}
                                                            >
                                                                {t('SalesBalanceSheet.last')}
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row className={"mb-3"}>
                                                    <Col md={12}>
                                                        {patientHistoryReferrals.some((o) => doctorDetails?.amka !== o?.doctorAMKA) && (
                                                            <span>(*) Με πορτοκαλί χρωματίζονται τα παραπεμπτικά άλλων γιατρών</span>
                                                        )}
                                                        <table className={"simpleClassicTable prescriptionTable"}>
                                                            <colgroup>
                                                                <col span={1} style={{width: "15%"}}></col>
                                                                <col span={1} style={{width: "15%"}}></col>
                                                                <col span={1} style={{width: "20%"}}></col>
                                                                <col span={1} style={{width: "35%"}}></col>
                                                                <col span={1} style={{width: "15%"}}></col>
                                                            </colgroup>
                                                            <thead>
                                                            <tr>
                                                                <th>Barcode</th>
                                                                <th>Ημερομηνία Συντ/σης</th>
                                                                <th>Διαγνώσεις</th>
                                                                <th>Εξετάσεις</th>
                                                                <th>Κατάσταση</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {patientHistoryReferrals.map((o, idx) => (
                                                                <tr key={`copyPrescr-${idx}`} onClick={() => onReferralSelect(o)}
                                                                    style={{backgroundColor: doctorDetails?.amka !== o?.doctorAMKA ? "#ffe2b3" : ""}}>
                                                                    <td>{o.barcode}</td>
                                                                    <td>{o.issueDate}</td>
                                                                    <td>{textToLines(o.diagnoses)}</td>
                                                                    <td>{textToLines(o.exams)}</td>
                                                                    <td>{o.status}</td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </Col>
                                                </Row>
                                            </React.Fragment>
                                        ) : (
                                            <span>Δεν βρέθηκαν παραπεμπτικά</span>
                                        )}
                                    </Col>
                                </Row>
                            </React.Fragment>
                        )}
                        <BiometricsModal
                            show={biometricsModal}
                            setShow={setBiometricsModal}
                            amka={patientHistory.amka}
                        />
                        <PatientDetailsModal
                            show={showPatientDetails}
                            setShow={setShowPatientDetails}
                            amka={patientHistory.amka}
                            disabled={true}
                        />
                    </React.Fragment>
                )}
            </React.Fragment>
        </div>
    )
}

export default PatientHistory
